import {
  Alert,
  Button,
  CircularProgress,
  List,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { useEffect, useState } from "react"
import {
  type ServiceAccount,
  useCreateServiceAccountMutation,
} from "../../services/serviceAccountsApi"
import { NewApiKeyDialog } from "./NewApiKeyDialog"
import { ServiceAccountRow } from "./ServiceAccountRow"
import { getErrorMessage } from "./utils"

type ServiceAccountsSectionProps = {
  activeOrgId: string | null
  activeOrgName: string | null
  isOrgServiceAdmin: boolean
  isLoading: boolean
  loadErrorMessage: string | null
  serviceAccounts: ServiceAccount[]
}

export function ServiceAccountsSection({
  activeOrgId,
  activeOrgName,
  isOrgServiceAdmin,
  isLoading,
  loadErrorMessage,
  serviceAccounts,
}: ServiceAccountsSectionProps) {
  const [createServiceAccount, { isLoading: isSubmitting }] =
    useCreateServiceAccountMutation()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [apiKeyError, setApiKeyError] = useState<string | null>(null)
  const [apiKeySuccessMessage, setApiKeySuccessMessage] = useState<
    string | null
  >(null)
  const [newApiKeyValue, setNewApiKeyValue] = useState<string | null>(null)
  const manageServiceAccountsTooltip = isOrgServiceAdmin
    ? ""
    : "Requires the organization service admin role."

  useEffect(() => {
    setSubmitError(null)
    setSuccessMessage(null)
    setApiKeyError(null)
    setApiKeySuccessMessage(null)
    setNewApiKeyValue(null)
  }, [activeOrgId])

  const handleCreateServiceAccount = async () => {
    if (!isOrgServiceAdmin) {
      setSubmitError(
        "You need the organization service admin role to create service accounts."
      )
      return
    }

    if (!activeOrgId) {
      setSubmitError(
        "Choose an active organization before creating a service account."
      )
      return
    }

    setSubmitError(null)
    setSuccessMessage(null)

    try {
      const createdServiceAccountResponse = await createServiceAccount({
        org_id: activeOrgId,
      }).unwrap()
      const createdServiceAccount = createdServiceAccountResponse.data

      setSuccessMessage(
        `Created service account "${createdServiceAccount.label}" for ${activeOrgName}.`
      )
    } catch (error) {
      setSubmitError(
        getErrorMessage(error) ?? "Unable to create the service account."
      )
    }
  }

  const handleApiKeyActionStarted = () => {
    setSubmitError(null)
    setSuccessMessage(null)
    setApiKeyError(null)
    setApiKeySuccessMessage(null)
    setNewApiKeyValue(null)
  }

  const handleApiKeyError = (message: string) => {
    setApiKeyError(message)
    setApiKeySuccessMessage(null)
  }

  const handleApiKeyDeleted = (message: string) => {
    setApiKeyError(null)
    setApiKeySuccessMessage(message)
    setNewApiKeyValue(null)
  }

  const handleApiKeyGenerated = (message: string, apiKeyValue: string) => {
    setApiKeyError(null)
    setApiKeySuccessMessage(message)
    setNewApiKeyValue(apiKeyValue)
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Service Account</Typography>

        {loadErrorMessage && <Alert severity="error">{loadErrorMessage}</Alert>}
        {submitError && <Alert severity="error">{submitError}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}
        {apiKeyError && <Alert severity="error">{apiKeyError}</Alert>}
        {apiKeySuccessMessage && (
          <Alert severity="success">{apiKeySuccessMessage}</Alert>
        )}

        {isLoading ? (
          <Stack alignItems="center" direction="row" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={20} />
            <Typography color="textSecondary">
              Loading service accounts...
            </Typography>
          </Stack>
        ) : serviceAccounts.length === 0 ? (
          <Stack spacing={2}>
            <Alert severity="info">
              This organization does not have a service account yet.
            </Alert>
            <Typography color="textSecondary" variant="body2">
              Create the organization&apos;s service account. Username and email
              are generated by the backend.
            </Typography>
            <Stack direction="row" justifyContent="flex-start">
              <Tooltip title={manageServiceAccountsTooltip}>
                <span>
                  <Button
                    disabled={
                      isSubmitting || !activeOrgId || !isOrgServiceAdmin
                    }
                    onClick={() => {
                      void handleCreateServiceAccount()
                    }}
                    variant="contained"
                  >
                    {isSubmitting ? "Creating..." : "Create Service Account"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        ) : (
          <List disablePadding>
            {serviceAccounts.map((serviceAccount, index) => {
              return (
                <ServiceAccountRow
                  activeOrgId={activeOrgId}
                  isOrgServiceAdmin={isOrgServiceAdmin}
                  index={index}
                  key={serviceAccount.service_account_id}
                  onApiKeyActionStarted={handleApiKeyActionStarted}
                  onApiKeyDeleted={handleApiKeyDeleted}
                  onApiKeyError={handleApiKeyError}
                  onApiKeyGenerated={handleApiKeyGenerated}
                  serviceAccount={serviceAccount}
                />
              )
            })}
          </List>
        )}
      </Stack>

      <NewApiKeyDialog
        apiKeyValue={newApiKeyValue}
        onClose={() => {
          setNewApiKeyValue(null)
        }}
      />
    </Paper>
  )
}
